"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Me, api, HttpError } from "@/lib/api";
import { FormatSubscriber, FormatToggle, NumberFormatProvider } from "@/components/NumberFormatContext";
import { AggregateCodesProvider } from "@/components/aggregate-codes";

type NavItem = {
  href: string;
  label: string;
  adminOnly?: boolean;       // visible to admin OR super_admin (+ manager unless strictAdmin)
  superAdminOnly?: boolean;  // visible to super_admin only
  strictAdmin?: boolean;     // when adminOnly is true, ALSO exclude manager (the Admin panel)
  external?: boolean;
  // A collapsible group: `href` is used only as a routing prefix for the
  // active-state + default-open check; the group renders its children
  // indented under a foldable header rather than as a link. Children
  // inherit the group's role gate.
  children?: { href: string; label: string }[];
};

// Starlette-Admin runs on the backend origin (a different port from this
// Next app), not behind the /api proxy. Linking there via a relative
// "/admin" makes the backend's trailing-slash + login redirects leak its
// own host (127.0.0.1:<port>) back to the browser. So point the link at
// the backend's real public URL, sourced per-host from kanhojiconfig.ini
// ([CHAKRA_DASHBOARD] admin_url) and inlined here as NEXT_PUBLIC_ADMIN_URL.
const ADMIN_URL = process.env.NEXT_PUBLIC_ADMIN_URL || "/admin";

// Reports + data-freshness were admin-only through Phase 3; widened to
// any authenticated user in chakra_dashboard#125. Backend endpoints
// filter by allowed_account_ids from deps.py, so a user with one
// account sees a one-column portfolio summary, and a user with zero
// accounts gets an empty state on the page — no 403. /admin still
// gates strictly.
const NAV: NavItem[] = [
  { href: "/", label: "Accounts" },
  { href: "/positions/matrix", label: "Open lots (cross-account)" },
  { href: "/reports/portfolio-daily", label: "Portfolio summary (Daily)" },
  { href: "/reports/portfolio-summary", label: "Portfolio summary (Aggregate)" },
  { href: "/reports/portfolio-summary-multitrade", label: "Portfolio summary (Split)" },
  { href: "/reports/per-broker-dealer", label: "Per-broker rollup" },
  // Pitchbook — investor tear-sheets. Manager-tier and above (adminOnly
  // without strictAdmin lets manager in). Collapsible group; Institutional
  // ships first, HNI is a stub. See chakra_dashboard#146.
  {
    href: "/pitchbook",
    label: "Pitchbook",
    adminOnly: true,
    children: [
      { href: "/pitchbook/institutional", label: "Institutional" },
      { href: "/pitchbook/hni", label: "HNI" },
    ],
  },
  { href: "/admin-tools/data-freshness", label: "Data freshness" },
  { href: ADMIN_URL, label: "Admin", adminOnly: true, strictAdmin: true, external: true },
];

// One nav node — leaf Link or collapsible group. Shared by the desktop
// sidebar and the mobile drawer so the group logic lives in one place.
function NavNode({
  item,
  pathname,
  mobile = false,
}: {
  item: NavItem;
  pathname: string;
  mobile?: boolean;
}) {
  const linkCls = (active: boolean, extra = "") =>
    `group flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] transition ${
      mobile ? "min-h-11 " : ""
    }${extra}${active ? "text-foreground" : "text-mute hover:text-foreground"}`;

  if (item.children) {
    const groupActive = pathname.startsWith(item.href);
    return (
      <details open={groupActive} className="group/nav flex flex-col">
        <summary className={`${linkCls(groupActive)} cursor-pointer list-none marker:hidden [&::-webkit-details-marker]:hidden`}>
          <span className={`h-px w-3 transition ${groupActive ? "bg-accent" : "bg-rule group-hover:bg-foreground"}`} />
          {item.label}
          <span aria-hidden className="ml-auto text-mute transition group-open/nav:rotate-90">›</span>
        </summary>
        <div className="mt-2 flex flex-col gap-2 pl-5">
          {item.children.map((c) => {
            const active = pathname === c.href || pathname.startsWith(c.href + "/");
            return (
              <Link key={c.href} href={c.href} className={linkCls(active)}>
                <span className={`h-px w-2 transition ${active ? "bg-accent" : "bg-rule group-hover:bg-foreground"}`} />
                {c.label}
              </Link>
            );
          })}
        </div>
      </details>
    );
  }

  const active = item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
  return (
    <Link
      href={item.href}
      target={item.external ? "_blank" : undefined}
      className={linkCls(active)}
    >
      <span className={`h-px w-3 transition ${active ? "bg-accent" : "bg-rule group-hover:bg-foreground"}`} />
      {item.label}
    </Link>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    api.me()
      .then(setMe)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 401) {
          router.replace("/login?next=" + encodeURIComponent(window.location.pathname));
        }
      })
      .finally(() => setLoading(false));
  }, [router]);

  // Close drawer on route change so taps on nav links don't leave it open.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  async function onLogout() {
    await api.logout();
    router.replace("/login");
    router.refresh();
  }

  const isAdminTier = me?.user_group === "admin" || me?.user_group === "super_admin";
  const isSuperAdmin = me?.user_group === "super_admin";
  const isManager = me?.user_group === "manager";
  // Manager tier — read-only view across all accounts + cross-account
  // reports. Sees everything admin sees EXCEPT items flagged strictAdmin
  // (SQLAdmin panel today). Admin actions the manager reaches from a
  // shared page (Data freshness refresh buttons, add-margin) are hidden
  // at the component level, not the nav.
  const items = NAV.filter((n) => {
    if (n.superAdminOnly && !isSuperAdmin) return false;
    if (n.adminOnly) {
      if (isAdminTier) return true;
      if (isManager && !n.strictAdmin) return true;
      return false;
    }
    return true;
  });

  return (
    <NumberFormatProvider>
    <AggregateCodesProvider>
    <div className="flex min-h-screen">
      {/* Sidebar — narrow, monospaced, terminal-style. Desktop only. */}
      <aside className="hidden w-56 shrink-0 flex-col justify-between border-r border-rule px-6 py-8 md:flex">
        <div>
          <div className="mb-12">
            <div className="display text-2xl leading-none">Chakra</div>
            <div className="eyebrow mt-1">Strategy Desk</div>
          </div>
          <nav className="flex flex-col gap-3 text-sm">
            {items.map((n) => (
              <NavNode key={n.href} item={n} pathname={pathname} />
            ))}
          </nav>
        </div>
        {me && (
          <div className="space-y-2 border-t border-rule pt-4">
            <div className="font-mono text-[11px] uppercase tracking-widest text-mute">
              {me.user_group}
            </div>
            <div className="break-all text-xs text-foreground">{me.email}</div>
            <div className="pt-1">
              <FormatToggle />
            </div>
            <Link
              href="/settings"
              className="block eyebrow text-mute transition hover:text-foreground"
            >
              ↳ Settings
            </Link>
            <button
              onClick={onLogout}
              className="eyebrow text-mute transition hover:text-foreground"
            >
              ↳ Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Mobile top bar — hamburger + brand. Sign-out moved into drawer. */}
      <header className="fixed top-0 left-0 right-0 z-20 flex h-14 items-center justify-between border-b border-rule bg-background/80 px-4 backdrop-blur md:hidden">
        <button
          type="button"
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          aria-controls="mobile-nav-drawer"
          onClick={() => setDrawerOpen(true)}
          className="-ml-2 flex h-11 w-11 items-center justify-center text-foreground"
        >
          {/* Hamburger — three hairlines matching the terminal aesthetic. */}
          <span className="flex flex-col gap-1.5" aria-hidden>
            <span className="block h-px w-5 bg-foreground" />
            <span className="block h-px w-5 bg-foreground" />
            <span className="block h-px w-5 bg-foreground" />
          </span>
        </button>
        <Link href="/" className="display text-lg leading-none">Chakra</Link>
        {/* Spacer for symmetry — keeps brand centered without an extra button. */}
        <span className="h-11 w-11" aria-hidden />
      </header>

      {/* Mobile drawer — full-height slide-in from the left. Mirrors the
          desktop sidebar's contents (nav + user identity + FormatToggle +
          sign out) so the entire app is reachable below the md breakpoint. */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-background/60 backdrop-blur-sm md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside
            id="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="fixed inset-y-0 left-0 z-40 flex w-72 max-w-[85vw] flex-col justify-between border-r border-rule bg-background px-6 py-6 md:hidden"
          >
            <div>
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <div className="display text-2xl leading-none">Chakra</div>
                  <div className="eyebrow mt-1">Strategy Desk</div>
                </div>
                <button
                  type="button"
                  aria-label="Close navigation menu"
                  onClick={() => setDrawerOpen(false)}
                  className="-mr-2 flex h-11 w-11 items-center justify-center text-mute hover:text-foreground"
                >
                  <span aria-hidden className="text-xl leading-none">×</span>
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {items.map((n) => (
                  <NavNode key={n.href} item={n} pathname={pathname} mobile />
                ))}
              </nav>
            </div>
            {me && (
              <div className="space-y-3 border-t border-rule pt-4">
                <div className="font-mono text-[11px] uppercase tracking-widest text-mute">
                  {me.user_group}
                </div>
                <div className="break-all text-xs text-foreground">{me.email}</div>
                <div className="pt-1">
                  <FormatToggle />
                </div>
                <Link
                  href="/settings"
                  className="flex min-h-11 items-center eyebrow text-mute transition hover:text-foreground"
                >
                  ↳ Settings
                </Link>
                <button
                  onClick={onLogout}
                  className="flex min-h-11 items-center eyebrow text-mute transition hover:text-foreground"
                >
                  ↳ Sign out
                </button>
              </div>
            )}
          </aside>
        </>
      )}

      {/* min-w-0 lets child containers (Table primitive's overflow-x-auto
          wrapper) actually clip horizontally; without it, `flex-1` defaults
          to min-width:auto and wide tables push the main column past viewport
          width on small screens. overflow-x-hidden is belt-and-braces — even
          when a child correctly scrolls internally, browsers still bump
          documentElement.scrollWidth from wide <table> content boxes, and
          this forbids the document itself from scrolling horizontally. */}
      <main className="min-w-0 flex-1 overflow-x-hidden px-6 pb-16 pt-20 md:px-10 md:py-12 md:pt-12">
        {/* FormatSubscriber wraps the page tree so a click on
            <FormatToggle> re-renders every fmtRupees(...) call site
            without a manual page reload. See NumberFormatContext for
            why this cascade is needed (children prop is a stable
            reference; direct-child pages don't subscribe otherwise). */}
        <FormatSubscriber>
          {loading ? <p className="eyebrow text-mute">Loading…</p> : children}
        </FormatSubscriber>
      </main>
    </div>
    </AggregateCodesProvider>
    </NumberFormatProvider>
  );
}
